import React, { useState, useEffect } from "react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import LiveChat from "../components/LiveChat";

import HelpArticleModal from "../components/HelpArticleModal";
import axios from "axios";
import { 
  MessageSquare, 
  Mail, 
  Phone, 
  FileText, 
  Search,
  Clock,
  Eye,
  ThumbsUp,
  ArrowRight,
  HeadphonesIcon,
  BookOpen,
  Users,
  CheckCircle,
  ExternalLink
} from "lucide-react";

const Help = () => {
  const [showLiveChat, setShowLiveChat] = useState(false);
  const [showArticleModal, setShowArticleModal] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [articles, setArticles] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [contactForm, setContactForm] = useState({
    name: "",
    email: "",
    subject: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState("");

  const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  useEffect(() => {
    fetchArticles();
  }, []);

  const fetchArticles = async () => {
    try {
      const response = await axios.get(`${API}/api/help/articles`);
      setArticles(response.data.articles);
    } catch (error) {
      console.error('Error fetching articles:', error);
    }
  };



  const handleArticleClick = async (article) => {
    try {
      const response = await axios.get(`${API}/api/help/articles/${article.id}`);
      setSelectedArticle(response.data);
      setShowArticleModal(true);
    } catch (error) {
      console.error('Error fetching article details:', error);
    }
  };

  const handleArticleVote = async (articleId, helpful) => {
    try {
      await axios.post(`${API}/api/help/articles/${articleId}/vote`, null, {
        params: { helpful }
      });
    } catch (error) {
      console.error('Error voting on article:', error);
    }
  };

  const handleEmailSupport = () => {
    const subject = encodeURIComponent("FormForgePro Support Request");
    const body = encodeURIComponent("Hi FormForgePro Support Team,\n\nI need assistance with:\n\n\nPlease contact me at your earliest convenience.\n\nThank you!");
    window.location.href = `mailto:support@formforgepro.com?subject=${subject}&body=${body}`;
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/api/contact`, contactForm);
      setSubmitMessage("Thank you! Your message has been sent successfully. We'll get back to you within 24 hours.");
      setContactForm({ name: "", email: "", subject: "", message: "" });
    } catch (error) {
      setSubmitMessage("Sorry, there was an error sending your message. Please try again or contact us directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const filteredArticles = articles.filter(article =>
    article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    article.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
    article.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const quickStats = [
    { label: "Average Response Time", value: "< 1 hour", icon: Clock },
    { label: "Customer Satisfaction", value: "99.2%", icon: ThumbsUp },
    { label: "24/7 Support", value: "Available", icon: HeadphonesIcon },
    { label: "Help Articles", value: "50+", icon: BookOpen }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-teal-50 to-cyan-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
            How Can We Help You?
          </h1>
          <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
            Get instant support through live chat, browse our help articles, 
            or contact our expert support team directly.
          </p>

          {/* Quick Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
            {quickStats.map((stat, index) => (
              <div key={index} className="bg-white rounded-lg p-4 shadow-sm">
                <div className="flex items-center justify-center mb-2">
                  <stat.icon className="w-6 h-6 text-teal-600" />
                </div>
                <div className="text-lg font-bold text-gray-800">{stat.value}</div>
                <div className="text-sm text-gray-600">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Support Options */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-800 mb-4">Get Support Your Way</h2>
            <p className="text-xl text-gray-600">Choose the support method that works best for you</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Live Chat */}
            <Card className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => setShowLiveChat(true)}>
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MessageSquare className="w-8 h-8 text-teal-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">Live Chat</h3>
                <p className="text-gray-600 text-sm mb-4">
                  Chat with our support team in real-time for immediate assistance
                </p>
                <Badge className="bg-green-100 text-green-800">
                  <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                  Online Now
                </Badge>
              </CardContent>
            </Card>

            {/* Email Support */}
            <Card className="hover:shadow-lg transition-shadow cursor-pointer" onClick={handleEmailSupport}>
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">Email Support</h3>
                <p className="text-gray-600 text-sm mb-4">
                  Send us an email and get a detailed response within 24 hours
                </p>
                <Badge variant="outline" className="text-blue-600 border-blue-300">
                  support@formforgepro.com
                </Badge>
              </CardContent>
            </Card>

            {/* Help Center & Resources */}
            <Card className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => window.scrollTo({ top: document.getElementById('help-articles')?.offsetTop || 0, behavior: 'smooth' })}>
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <BookOpen className="w-8 h-8 text-purple-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">Help Center & Resources</h3>
                <p className="text-gray-600 text-sm mb-4">
                  Browse our comprehensive library of guides, tutorials, and FAQs
                </p>
                <Badge variant="outline" className="text-purple-600 border-purple-300">
                  50+ Articles
                </Badge>
              </CardContent>
            </Card>


          </div>
        </div>
      </section>



      {/* Help Articles Section */}
      <section id="help-articles" className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-800 mb-4">Browse Help Topics</h2>
            <p className="text-xl text-gray-600">Find detailed answers to common questions</p>
          </div>

          {/* Search Bar */}
          <div className="relative max-w-2xl mx-auto mb-8">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <Input
              type="text"
              placeholder="Search help articles..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-3 text-lg border-gray-300 rounded-lg"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {filteredArticles.map((article) => (
              <Card key={article.id} className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => handleArticleClick(article)}>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-3">
                    <Badge variant="outline" className="text-teal-600 border-teal-300">
                      {article.category}
                    </Badge>
                    <div className="flex items-center text-sm text-gray-500">
                      <Eye className="w-4 h-4 mr-1" />
                      {article.views}
                    </div>
                  </div>
                  
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">{article.title}</h3>
                  <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                    {article.content.substring(0, 150)}...
                  </p>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center text-sm text-gray-500">
                      <ThumbsUp className="w-4 h-4 mr-1" />
                      {article.helpful_votes} helpful
                    </div>
                    <Button variant="ghost" size="sm" className="text-teal-600 hover:text-teal-800">
                      Read More
                      <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-800 mb-4">Still Need Help?</h2>
            <p className="text-xl text-gray-600">Send us a message and we'll get back to you within 24 hours</p>
          </div>

          <Card>
            <CardContent className="p-8">
              <form onSubmit={handleContactSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
                    <Input
                      type="text"
                      value={contactForm.name}
                      onChange={(e) => setContactForm({...contactForm, name: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                    <Input
                      type="email"
                      value={contactForm.email}
                      onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Subject</label>
                  <Input
                    type="text"
                    value={contactForm.subject}
                    onChange={(e) => setContactForm({...contactForm, subject: e.target.value})}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Message</label>
                  <Textarea
                    rows={6}
                    value={contactForm.message}
                    onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
                    required
                  />
                </div>

                {submitMessage && (
                  <div className={`p-4 rounded-lg ${submitMessage.includes('Thank you') ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
                    {submitMessage}
                  </div>
                )}

                <Button 
                  type="submit" 
                  disabled={isSubmitting}
                  className="w-full bg-teal-500 hover:bg-teal-600 text-white font-semibold py-3"
                >
                  {isSubmitting ? "Sending..." : "Send Message"}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Modals */}
      <LiveChat 
        isOpen={showLiveChat} 
        onClose={() => setShowLiveChat(false)} 
      />
      

      
      <HelpArticleModal
        article={selectedArticle}
        isOpen={showArticleModal}
        onClose={() => setShowArticleModal(false)}
        onVote={handleArticleVote}
      />
    </div>
  );
};

export default Help;